import { Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { debounceTime, Subject, takeUntil } from "rxjs";
import { CategoryLevelEnum } from "../../models/category.v2";
import { IGenericSelectDto } from "../../models/Interfaces";
import { IProductList, ProductListFilter, StatusEnum } from "../../models/product-v2";
import { BrandService } from "../../pages/members/brand/services/brand.service";
import { CategoryV2Service } from "../../pages/members/category-v2/services/category.v2.service";
import { CollectionProductDto, CollectionServiceProxy, FileImportDto } from "../../pages/members/collection/services/collection.service";
import { MessageService } from "../../util/message.service";
import { ModalService } from "../../util/modal.service";
import { ProductV2Service } from "../../pages/members/product-v2/services/product.v2.service";

@Component({
    selector: 'app-collection-table',
    templateUrl: 'collection-table.component.html',
    styleUrl: 'collection-table.component.scss'
})
export class CollectionTableComponent implements OnInit, OnDestroy, OnChanges {

    tab: number = 1;
    destroy$ = new Subject<any>();
    brandSearch$ = new Subject<string>();
    categorySearch$ = new Subject<string>();
    categories: IGenericSelectDto<string>[] = [];
    brands: IGenericSelectDto<string>[] = [];
    id: string = '';
    addedProductIds: Set<string> = new Set();

    @Input() storeId: string = '';
    @Input() query: string = '';

    status: IGenericSelectDto<string>[] = Object.values(StatusEnum).map(x => ({
        key: x.toString(),
        value: x
    }));

    products: IProductList[] = [];
    collectionProducts: CollectionProductDto[] = [];
    total: number = 0;
    totalCollectionProducts: number = 0;
    categorySearchTerm = '';
    brandSearchTerm = '';
    selectedBrand: string = '';
    selectedCategory: string = '';
    selectedStatus: string = '';

    showExportModal = false;

    emailInput: string = '';
    emailError: string = '';

    exportOptions = {
        format: 'xlsx',
        download: true,
        sendByEmail: false,
        emailList: [] as string[],
    };

    categoryPage = 1;
    hasMoreCategories = true;
    isLoadingMoreCategories = false;
    previousCategory: string | null = null;

    brandPage = 1;
    hasMoreBrands = true;
    isLoadingMoreBrands = false;
    previousBrand: string | null = null;

    constructor(
        private readonly _service: CollectionServiceProxy,
        private readonly _productService: ProductV2Service,
        private readonly _categoryService: CategoryV2Service,
        private readonly _brandService: BrandService,
        private readonly route: ActivatedRoute,
        private readonly _messageService: MessageService,
        private readonly _modalService: ModalService
        
    ) {
    }

    ngOnDestroy(): void {
        this.destroy$.complete();
    }

    ngOnInit() {
        this.initData();

        this.brandSearch$
            .pipe(debounceTime(800), takeUntil(this.destroy$))
            .subscribe(term => {
                this.brandSearchTerm = term;
                this.brandPage = 0;
                this.hasMoreBrands = true;

                this._brandService.list({
                    storeId: this.storeId,
                    page: 0,
                    size: 10,
                    name: term
                })
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(res => {
                        this.brands = res.items.map(x => ({
                            key: x.name,
                            value: x.id
                        }));

                        this.hasMoreBrands = this.brands.length < res.page.totalElements;
                    });
            });


        this.categorySearch$
            .pipe(debounceTime(400), takeUntil(this.destroy$))
            .subscribe(term => {
                this.categorySearchTerm = term;
                this.categoryPage = 0;
                this.hasMoreCategories = true;

                this._categoryService.list(this.storeId, {
                    size: 10,
                    page: 0,
                    name: term,
                    level: CategoryLevelEnum.LEAF
                }).pipe(takeUntil(this.destroy$))
                    .subscribe(res => {
                        this.categories = res.items.map(x => ({
                            key: x.shortName,
                            value: x.id
                        }));

                        this.hasMoreCategories = this.categories.length < res.page.totalElements;
                    });
            });

    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['query'] && !changes['query'].firstChange) {
            this.selectPage(1);
        }
    }

    initData() {
        this.route.params.subscribe({
            next: (params) => {
                if (params['id']) {
                    this.id = params['id'];

                    this._service.findAllProducts(this.storeId, this.id, 0, 9999).subscribe({
                        next: (res) => {
                            if (res.items) {
                                this.addedProductIds = new Set(res.items.map(p => p.productId));
                                this.collectionProducts = res.items;
                                this.totalCollectionProducts = this.collectionProducts.length;
                            }
                        }
                    })
                }
            }
        });
        this._productService.list(this.storeId, { page: 0, size: 7 }).subscribe({
            next: (res) => {
                if (res) {
                    this.products = res.items;
                    this.total = res.page.totalElements;
                }
            }
        })

        this._categoryService.list(this.storeId, {
            size: 10,
            page: 0,
            level: CategoryLevelEnum.LEAF
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    this.categories = res.items.map(x => ({
                        key: x.shortName,
                        value: x.id
                    }))
                }
            })

        this._brandService.list({
            storeId: this.storeId,
            page: 0,
            size: 10
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    this.brands = res.items.map(x => ({
                        key: x.name,
                        value: x.id
                    }))
                }
            })
    }
    onCategorySelect($event: IGenericSelectDto<string>) {
        if (this.previousCategory === $event.value) return;

        this.previousCategory = $event.value;
        this.selectedCategory = $event.value;
        this.selectPage(1);
    }

    onStatusSelect($event: any) {
        this.selectedStatus = $event.value;
        this.selectPage(1);
    }

    onBrandSelect($event: IGenericSelectDto<string>) {
        if (this.previousBrand === $event.value) return;

        this.previousBrand = $event.value;
        this.selectedBrand = $event.value;
        this.selectPage(1);
    }

    selectPage(page: number) {

        let params: ProductListFilter = {
            page: page - 1,
            size: 7,
            brandId: this.selectedBrand,
            categoryId: this.selectedCategory,
            status: this.selectedStatus
        }
        if (this.query && this.query.trim() !== '') {
            params.q = this.query.trim();
        }
        this._productService.list(this.storeId, params).subscribe({
            next: (res) => {
                if (res) {
                    this.products = res.items;
                    this.total = res.page.totalElements;
                }
            }
        });
    }
    loadMoreCategories() {
        if (this.isLoadingMoreCategories || !this.hasMoreCategories) return;

        this.isLoadingMoreCategories = true;

        this._categoryService.list(this.storeId, {
            size: 10,
            page: this.categoryPage,
            name: this.categorySearchTerm,
            level: CategoryLevelEnum.LEAF
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    const newCategories = res.items.map(x => ({
                        key: x.shortName,
                        value: x.id
                    }));

                    this.categories = [...this.categories, ...newCategories];

                    const total = res.page.totalElements;
                    this.hasMoreCategories = this.categories.length < total;
                    if (this.hasMoreCategories) {
                        this.categoryPage++;
                    }

                    this.isLoadingMoreCategories = false;
                },
                error: () => {
                    this.isLoadingMoreCategories = false;
                }
            });
    }

    loadMoreBrands() {
        if (this.isLoadingMoreBrands || !this.hasMoreBrands) return;

        this.isLoadingMoreBrands = true;

        this._brandService.list({
            storeId: this.storeId,
            page: this.brandPage,
            size: 10,
            name: this.brandSearchTerm
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    const newBrands = res.items.map(x => ({
                        key: x.name,
                        value: x.id
                    }));

                    this.brands = [...this.brands, ...newBrands];

                    const total = res.page.totalElements;
                    this.hasMoreBrands = this.brands.length < total;

                    if (this.hasMoreBrands) {
                        this.brandPage++;
                    }

                    this.isLoadingMoreBrands = false;
                },
                error: () => {
                    this.isLoadingMoreBrands = false;
                }
            });
    }


    onCategorySearch(term: string) {
        this.categorySearch$.next(term);
    }

    onBrandSearch(term: string) {
        this.brandSearch$.next(term);
    }

    setProduct(id: string) {
        this._service.addProducts(this.storeId, this.id, [id]).subscribe({
            next: () => {
                this.addedProductIds.add(id);
                const product = this.products.find(p => p.id === id);
                if (product) {
                    const collectionProduct = CollectionProductDto.fromJS({
                        productId: product.id,
                        name: product.name,
                        imageUrl: product.images?.[0]?.url || '',
                    });
                    this.collectionProducts.push(collectionProduct);
                    this.totalCollectionProducts = this.collectionProducts.length
                }
                this._messageService.success('Producto agregado correctamente.');
            },
            error: (e) => {
                console.error(e);
                this._messageService.error(e.message);
            }
        });
    }


    removeProduct(id: string) {
        this._service.removeProducts(this.storeId, this.id, id).subscribe({
            next: () => {
                this.addedProductIds.delete(id);
                const index = this.collectionProducts.findIndex(p => p.productId === id);
                if (index !== -1) {
                    this.collectionProducts.splice(index, 1);
                    this.totalCollectionProducts = this.collectionProducts.length;
                }
                this._messageService.success('Producto removido correctamente.');
            },
            error: (e) => {
                console.error(e);
                this._messageService.error(e.message);
            }
        });

    }

    isProductAdded(id: string): boolean {
        return this.addedProductIds.has(id);
    }

    openExportModal() {
        this.showExportModal = true;
    }
    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;

        if (!input.files || input.files.length === 0) {
            return;
        }

        const file = input.files[0];

        const reader = new FileReader();
        reader.onload = async () => {
            const base64 = (reader.result as string).split(',')[1];

            const fileImportDto = FileImportDto.fromJS({
                fileName: file.name,
                fileContent: base64,
            });

            try {
                const extension = file.name.split('.').pop()?.toLowerCase();
                if (extension === 'csv') {
                    await this._service.collectionController_importCollectionProductsFromCsv(this.storeId, this.id, fileImportDto).subscribe({
                        next: () => {
                            this._messageService.success('Importación realizada con éxito.');
                            this.initData();
                        },
                        error: (e) => {
                            this._messageService.error('Error durante la importación.');
                            console.log(e.message);
                        }
                    });
                } else if (extension === 'xlsx') {
                    await this._service.collectionController_importCollectionProductsFromExcel(this.storeId, this.id, fileImportDto).subscribe({
                        next: () => {
                            this._messageService.success('Importación realizada con éxito.');
                            this.initData();
                        },
                        error: (e) => {
                            this._messageService.error('Error durante la importación.');
                            console.log(e.message);
                        }
                    });
                } else {
                    this._messageService.error('Formato no soportado. Use .xlsx o .csv');
                    return;
                }


            } catch (e: any) {
                console.error(e);
                this._messageService.error('Error al importar archivo.');
            }
        };

        reader.readAsDataURL(file);
    }



    closeExportModal() {
        this.showExportModal = false;
        this.emailInput = '';
        this.emailError = '';
        this.exportOptions.emailList = [];
        this.exportOptions.sendByEmail = false;
        this.exportOptions.download = true;
        this.exportOptions.format = 'xlsx';
    }

    addEmail(event: Event) {
        event.preventDefault();
        const value = this.emailInput.trim().toLowerCase();
        this.emailInput = '';

        if (!value) return;

        const emails = value.split(',').map(e => e.trim()).filter(e => e);

        for (const email of emails) {
            if (!this.isValidEmail(email)) {
                this.emailError = `Correo inválido: ${email}`;
                return;
            }
            if (!this.exportOptions.emailList.includes(email)) {
                this.exportOptions.emailList.push(email);
            }
        }

        this.emailError = '';
    }

    removeEmail(email: string) {
        this.exportOptions.emailList = this.exportOptions.emailList.filter(e => e !== email);
    }

    isValidEmail(email: string): boolean {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    }

    confirmExport() {
        const { format, download, sendByEmail, emailList } = this.exportOptions;
      
        if (!download && !sendByEmail) {
          this._messageService.error('Seleccioná al menos una forma de exportación.');
          return;
        }
      
        if (sendByEmail && emailList.length === 0) {
          this._messageService.error('Ingresá al menos un correo válido para enviar.');
          return;
        }
      
        const recipients = sendByEmail ? emailList : undefined;
      
        const sub = format === 'xlsx'
          ? this._service.exportProductsToExcel(this.storeId, this.id, recipients)
          : this._service.exportProductsToCsv(this.storeId, this.id, recipients);
      
        sub.subscribe({
          next: (file) => {
            if (download) {
              this.downloadBase64(file.fileName, file.fileContent);
            }
            this._messageService.success('Exportación iniciada.');
            this.closeExportModal();
          },
          error: (err) => this.handleError(err)
        });
      }
      handleError(err) {
        if (err.status == 400) {
          this._modalService.showModalError('Ocurrio un error', 'Algunos caracteres no pudieron procesarse')
        } else {
          this._modalService.showModalGenerico(err)
        }
      }      

    downloadBase64(fileName: string, base64: string) {
        const link = document.createElement('a');
        link.href = `data:application/octet-stream;base64,${base64}`;
        link.download = fileName;
        link.click();
    }

    clearBrandFilter() {
        if (!this.selectedBrand) return;

        this.selectedBrand = '';
        this.previousBrand = null;
        this.selectPage(1);
    }

    clearCategoryFilter() {
        if (!this.selectedCategory) return;

        this.selectedCategory = '';
        this.previousCategory = null;
        this.selectPage(1);
    }

    clearStatusFilter() {
        this.selectedStatus = '';
        this.selectPage(1);
    }
}